import apiCall from './apiCall';
import { ApiActions } from './apiActions';

// Storage keys
const CURRENCY_STORAGE_KEY = 'tripsy_currencies';
const CURRENCY_TIMESTAMP_KEY = 'tripsy_currencies_timestamp';
const SELECTED_CURRENCY_KEY = 'tripsy_selected_currency';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Default values
const DEFAULT_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$', dollarRate: 1 }
];

/**
 * Load currencies from storage or API
 */
export const loadCurrencies = async () => {
  // Check if we have cached currencies
  const cachedCurrencies = getCachedCurrencies();
  if (cachedCurrencies) {
    return cachedCurrencies;
  }
  
  try {
    // Check if user is authenticated (has token)
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      // User not authenticated, return defaults silently
      return DEFAULT_CURRENCIES;
    }
    
    // Fetch from API using the correct endpoint
    const response = await apiCall(`/api/model?action=${ApiActions.GET_SETTINGS}&key=supportedCurrencies`, {}, 'GET');
    
    if (response.success && response.data) {
      // Save to storage (only in browser)
      if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
        localStorage.setItem(CURRENCY_STORAGE_KEY, JSON.stringify(response.data.currencies));
        localStorage.setItem(CURRENCY_TIMESTAMP_KEY, Date.now().toString());
        localStorage.setItem(SELECTED_CURRENCY_KEY, response.data.defaultCurrency || 'USD');
      }
      
      return response.data.currencies;
    }
    
    return DEFAULT_CURRENCIES;
  } catch (error) {
    // Silently return defaults on error (don't log unauthorized errors)
    if (error.message && error.message.includes('Unauthorized')) {
      // User not authenticated, return defaults silently
      return DEFAULT_CURRENCIES;
    }
    // Only log non-auth errors
    console.error('Error loading currencies:', error);
    return DEFAULT_CURRENCIES;
  }
};

/**
 * Get currencies from cache if available and not expired
 */
export const getCachedCurrencies = () => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  
  try {
    const timestamp = localStorage.getItem(CURRENCY_TIMESTAMP_KEY);
    const currencies = localStorage.getItem(CURRENCY_STORAGE_KEY);
    
    if (!timestamp || !currencies) {
      return null;
    }
    
    // Check if cache is expired
    const now = Date.now();
    if (now - parseInt(timestamp) > CACHE_DURATION) {
      return null;
    }
    
    return JSON.parse(currencies);
  } catch (error) {
    console.error('Error getting cached currencies:', error);
    return null;
  }
};

/**
 * Get the currently selected currency
 */
export const getSelectedCurrency = () => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return 'USD';
  }
  return localStorage.getItem(SELECTED_CURRENCY_KEY) || 'USD';
};

/**
 * Set the selected currency
 */
export const setSelectedCurrency = (currencyCode) => {
  if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
    localStorage.setItem(SELECTED_CURRENCY_KEY, currencyCode);
  }
};

/**
 * Get all supported currencies (async - loads from cache or API)
 */
export const getAllCurrencies = async () => {
  return await loadCurrencies();
};

/**
 * Get currencies synchronously from cache (for use in forms)
 * Returns cached currencies or default if not available
 */
export const getCurrenciesSync = () => {
  const cached = getCachedCurrencies();
  if (cached && cached.length > 0) {
    return cached;
  }
  // Return default if cache is empty
  return DEFAULT_CURRENCIES;
};

/**
 * Get currency by code
 */
export const getCurrencyByCode = async (code) => {
  const currencies = await loadCurrencies();
  return currencies.find(c => c.code === code) || currencies[0];
};

/**
 * Convert amount from one currency to another
 */
export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  if (!amount || isNaN(parseFloat(amount))) {
    return 0;
  }
  
  const numericAmount = parseFloat(amount);
  
  // If same currency, no conversion needed
  if (fromCurrency === toCurrency) {
    return numericAmount;
  }
  
  const currencies = await loadCurrencies();
  
  // Find currency rates
  const fromCurrencyObj = currencies.find(c => c.code === fromCurrency);
  const toCurrencyObj = currencies.find(c => c.code === toCurrency);
  
  if (!fromCurrencyObj || !toCurrencyObj) {
    return numericAmount; // Return original if can't convert
  }
  
  // Use dollarRate directly - these represent how many USD one unit of currency is worth
  // Step 1: Convert source currency to USD
  const amountInUSD = numericAmount * fromCurrencyObj.dollarRate;
  // Step 2: Convert USD to target currency
  const convertedAmount = amountInUSD / toCurrencyObj.dollarRate;
  
  return parseFloat(convertedAmount.toFixed(2));
};

/**
 * Fetch historical exchange rates for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<object>} - Object with currency codes as keys and rates as values
 */
export const getHistoricalRates = async (date) => {
  try {
    // Validate date format
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error('Invalid date format. Use YYYY-MM-DD');
    }
    
    // Call the API to get historical rates
    const response = await apiCall(
      `/api/model?action=${ApiActions.GET_HISTORICAL_RATES}&date=${date}`,
      {},
      'GET'
    );
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch historical rates');
    }
    
    return response.rates || {};
  } catch (error) {
    console.error('Error fetching historical rates:', error);
    throw error;
  }
};

/**
 * Convert amount using historical exchange rates
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @param {object} historicalRates - Historical exchange rates
 * @returns {number} - Converted amount
 */
export const convertCurrencyWithHistoricalRates = (amount, fromCurrency, toCurrency, historicalRates) => {
  if (!amount || isNaN(parseFloat(amount))) {
    return 0;
  }
  
  const numericAmount = parseFloat(amount);
  
  // If same currency, no conversion needed
  if (fromCurrency === toCurrency) {
    return numericAmount;
  }
  
  // Check if we have the required rates
  if (!historicalRates) {
    console.warn('No historical rates provided, returning original amount');
    return numericAmount;
  }
  
  // Handle case where USD might not be explicitly included in rates
  if (fromCurrency === 'USD' && !historicalRates['USD']) {
    historicalRates['USD'] = { code: 'USD', dollarRate: 1 };
  }
  
  if (toCurrency === 'USD' && !historicalRates['USD']) {
    historicalRates['USD'] = { code: 'USD', dollarRate: 1 };
  }
  
  // Check again if we have the required rates
  if (!historicalRates[fromCurrency] || !historicalRates[toCurrency]) {
    console.warn(`Missing rate for ${!historicalRates[fromCurrency] ? fromCurrency : toCurrency}, returning original amount`);
    return numericAmount;
  }
  
  // Use historical rates for conversion
  const fromRate = historicalRates[fromCurrency].dollarRate;
  const toRate = historicalRates[toCurrency].dollarRate;
  
  // Step 1: Convert source currency to USD
  const amountInUSD = numericAmount * fromRate;
  // Step 2: Convert USD to target currency
  const convertedAmount = amountInUSD / toRate;
  
  return parseFloat(convertedAmount.toFixed(2));
};

const currencyService = {
  loadCurrencies,
  getCachedCurrencies,
  getSelectedCurrency,
  setSelectedCurrency,
  getAllCurrencies,
  getCurrencyByCode,
  convertCurrency,
  getHistoricalRates,
  convertCurrencyWithHistoricalRates
};

export default currencyService; 